import './DatePicker.css';

export type DatePickerDayState =
  | 'default'
  | 'hover'
  | 'current'
  | 'current-hover'
  | 'selected'
  | 'inactive'
  | 'range'
  | 'in-range-start'
  | 'in-range-end'
  | 'in-range-middle'
  | 'disabled';

/** Single day cell inside the DatePicker grid. Renders as a button or a static label. */
export type DatePickerDayProps = {
  label: string;
  state?: DatePickerDayState;
  /** When false, renders a non-interactive label (weekday headers). */
  interactive?: boolean;
  className?: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  tabIndex?: number;
};

export function DatePickerDay({
  label,
  state = 'default',
  interactive = true,
  className = '',
  onClick,
  onKeyDown,
  tabIndex,
}: DatePickerDayProps) {
  const isInactive = state === 'inactive';
  const isDisabled = state === 'disabled';
  const isRangeEndpoint = state === 'in-range-start' || state === 'in-range-end';
  const isRangeMiddle = state === 'in-range-middle';
  const isStaticVisualState =
    state === 'hover' ||
    state === 'selected' ||
    state === 'range' ||
    state === 'current-hover' ||
    isRangeEndpoint ||
    isRangeMiddle;
  const canInteract = interactive && !isInactive && !isDisabled && !isStaticVisualState;

  const classes = [
    'spark-date-picker-day',
    isInactive ? 'spark-date-picker-day-inactive' : '',
    isDisabled ? 'spark-date-picker-day-inactive' : '',
    state === 'current' || state === 'current-hover' ? 'spark-date-picker-day-current' : '',
    state === 'current-hover' ? 'spark-date-picker-day-current-hover' : '',
    state === 'selected' || isRangeEndpoint ? 'spark-date-picker-day-selected' : '',
    state === 'hover' ? 'spark-date-picker-day-hover' : '',
    state === 'range' || isRangeMiddle ? 'spark-date-picker-day-range' : '',
    canInteract || (interactive && isRangeEndpoint) || (interactive && isRangeMiddle)
      ? 'spark-date-picker-day-interactive'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (canInteract || (interactive && !isInactive && !isDisabled)) {
    return (
      <button
        type="button"
        className={classes}
        aria-label={label}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        disabled={isDisabled}
      >
        {label}
      </button>
    );
  }

  return <span className={classes}>{label}</span>;
}
